import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'
import type { KanbanColumn } from '@/shared/types'

interface UpdateColumnParams {
  featureId: string
  column: KanbanColumn
}

interface RpcResult {
  success: boolean
  error?: string
  from_column?: KanbanColumn
  to_column?: KanbanColumn
}

/**
 * Calls the Supabase RPC function to move a feature to a new column.
 * Uses SECURITY DEFINER to bypass RLS restrictions on non-backlog features.
 */
async function updateFeatureColumn({ featureId, column }: UpdateColumnParams): Promise<void> {
  const { data, error } = await supabase.rpc('move_feature_to_column', {
    p_feature_id: featureId,
    p_target_column: column,
  })

  if (error) {
    throw new Error(error.message)
  }

  const result = data as RpcResult
  if (!result.success) {
    throw new Error(result.error || 'Failed to move feature')
  }
}

export function useUpdateFeatureColumn() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateFeatureColumn,
    onSuccess: () => {
      // Invalidate feature queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['feature'] })
      queryClient.invalidateQueries({ queryKey: ['features'] })
      queryClient.invalidateQueries({ queryKey: ['featureByShortId'] })
    },
  })
}
